import { createAsyncStore } from "common/axios/asyncModel";
import { UserRepository } from "repository/userRepository";
import { saveUserInfo, clearAll, UserInfo } from "common/utils/secureStorage";
import { getAccessToken, decodeJwtPayload, markLoggedOut } from "common/utils/auth";

const ssoLogin = createAsyncStore();
const ssoSignup = createAsyncStore();
const ssoUser = createAsyncStore();

// SSO 로그인
const onSsoLogin = (params: { email: string; password: string; deviceId?: string; os?: string }) => {
    ssoLogin.onGetAll(() => UserRepository.onSsoLogin(params));
}

// SSO 회원가입
const onSsoSignup = (params: { email: string; password: string; nickname: string; deviceId?: string; os?: string }) => {
    ssoSignup.onGetAll(() => UserRepository.onSsoSignup(params));
}

// SSO 회원정보 조회
// - JWT payload.sub 에 암호화된 userId 가 들어있으므로 그걸 query 로 같이 전달
//   → 서버(wiz-sso)가 Mock 모드여도 내 실제 userId 기반으로 조회됨
//   (쿼리 누락 시 서버의 `getUserByUserNo(1L)` 하드코딩 분기로 빠져 Mock coolguy 가 섞여 들어옴)
const extractEncryptedUserIdFromJwt = (token: string): string | undefined => {
    try {
        const payload = decodeJwtPayload(token);
        return typeof payload?.sub === 'string' ? payload.sub : undefined;
    } catch { return undefined; }
};
const onGetSsoUser = () => {
    const accessToken = getAccessToken();
    if (accessToken) {
        const encryptedUserId = extractEncryptedUserIdFromJwt(accessToken);
        ssoUser.onGetAll(() => UserRepository.onGetSsoUser(accessToken, encryptedUserId));
    }
}

// 회원정보 저장 (암호화) - wiz-sso UserResponse 기반
// SSO 는 글로벌 user_no 를 돌려주므로 DOKOCREW 서비스 user_no 와 다를 수 있음.
// userNo 는 JWT payload 에서 우선 추출(DOKOCREW 서비스 정합), 실패 시 SSO 값 fallback.
const onSaveUserInfo = (rawData: any) => {
    // 게이트웨이 래핑 대응: { data: { nickName, ... } } 또는 직접 { nickName, ... }
    const data = rawData?.data?.nickName != null ? rawData.data
               : rawData?.data?.userId != null ? rawData.data
               : rawData;

    const rawNickname = data.nickName || data.nickname || '';
    let nickname = rawNickname;
    try { nickname = decodeURIComponent(rawNickname); } catch { /* 이미 디코딩됨 */ }

    // JWT 에서 DOKOCREW 서비스 userNo 추출 시도
    let crewUserNo: number | undefined;
    try {
        const token = getAccessToken();
        if (token) {
            const payload = decodeJwtPayload(token);
            if (payload?.userNo) crewUserNo = Number(payload.userNo);
        }
    } catch { /* JWT 파싱 실패 — SSO 값 fallback */ }

    const userNo = crewUserNo ?? data.userNo ?? data.user_no ?? 0;

    const userInfo: UserInfo = {
        userId: data.userId || '',           // SSO에서 암호화되어 옴
        userNo: userNo,                      // DOKOCREW 서비스 user_no (API 호출용 정답)
        nickname: nickname,                  // 닉네임
        ctn: data.ctn || '',                 // 본인 연락처
        age: data.age || '',                 // 생년월일/나이
        profileImgPath: data.profileImgPath || '',
        profileImgNo: data.profileImgNo || '',
        isMinor: data.isMinor || 'N',
        guardCtn: data.guardCtn || '',       // 보호자 연락처
        locale: data.locale || 'ko',
        distanceUnit: data.distanceUnit || 'km'
    };
    saveUserInfo(userInfo);
    sessionStorage.setItem('userNo', String(userNo));
}

// 로그아웃
const onLogout = () => {
    const token = getAccessToken();
    if (token) {
        UserRepository.onSsoLogout(token);
    }
    // 모든 인증 정보 삭제 (암호화된 사용자 정보 포함)
    clearAll();
    // localStorage 토큰도 삭제
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // 로그아웃 표시 — 자동으로 들어오는 토큰을 무시하게 합니다(auth.ts 설명 참고).
    markLoggedOut();
}

// 토큰 유효성 검사
const isAuthenticated = () => {
    return !!getAccessToken();
}

export const userStore = {
    ssoLogin,
    ssoSignup,
    ssoUser,
    onSsoLogin,
    onSsoSignup,
    onGetSsoUser,
    onSaveUserInfo,
    onLogout,
    isAuthenticated
}